import React, { useState, useEffect, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { Card } from 'react-bootstrap'
import SimulationFormHeader from './SimulationFormHeader'
import ErrorMessage from '../Shared/ErrorMessage'
import Input from '../Shared/Input'
import LoadingSpinner from '../Shared/LoadingSpinner'
import { initStateObject, objectReducer } from '../../reducers/default'
import { sendSimulationForm } from '../../api/simulation'
import Artist from './Artist'
import SetList from './Setlist'
import { registerLocale } from 'react-datepicker'
import de from 'date-fns/locale/de'
import KeyFactsForm from './KeyFactsForm'
import PrimaryButton, { SecondaryButton } from '../Shared/Button'
import DropDown from '../Shared/DropDown'
import { eventCategories } from './constants'

import CopyPasteContent from '../EventEditor/CopyPasteLineupSetlist/Content'

import InfoBox from '../Shared/InfoBox'
import SessionTimer from 'components/SessionTimer/SessionTimer'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import PortalModal from 'components/Shared/PortalModal'
import Textarea from 'components/Shared/Textarea'

registerLocale('de', de)

const formatData = (name, description, fields, lineups, setlists, tz, t) => {
    let ERROR = false
    let errMsg = t('common.errors.fillRequired')
    setlists.map((setlist) => {
        if (!setlist.artist_name || !setlist.track)
            ERROR = true
        return setlist
    })
    lineups.map((lineup) => {
        if (!lineup.artist_name || !lineup.artist_type)
            ERROR = true
        return lineup
    })
    const resp = fields.map((field) => {
        let { venue, capacity, date, time, hall, price, category, city } = field
        if (!venue || !capacity || !date || !time || !hall || !name || !category || !city)
            ERROR = true
        if ((capacity && (capacity < 0 || capacity % 1 !== 0)) || (price && price < 0)) {
            ERROR = true
            errMsg = t('Simulation.form.errors.invalidFields')
        }
        if (typeof capacity === 'string')
            capacity = parseInt(capacity)

        return {
            name, description, venue, capacity, date, time, city, category, hall, price, lineups, setlists, tz, status: 'RUNNING'
        }
    })

    if (ERROR) return { error: true, errMsg, resp: null }
    return { error: false, resp }
}

/**
 * Set simulation form with datapicker.
 * @Tags( simulation, api )
 * @Inputs( eventName, Date, Time, Venue, Hall, Capacity, Price, Artists, Tracks, Categories )
 * @NpmPackages( react-datepicker, date-fns ) 
 */
const SimulationForm = () => {
    const { addNotification } = useToast()
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [formStatus, updateFormStatus] = useReducer(objectReducer, initStateObject)
    const [error, setError] = useState('')
    const [accuracy, setAccuracy] = useState('0')
    const [startDate, setStartDate] = useState(null)
    const [tz, setTz] = useState('')

    const [simName, setSimName] = useState('')
    const [description, setDescription] = useState('')
    const [category, setCategory] = useState('')
    const [copyModalOpen, setCopyModalOpen] = useState(false)
    const [showCopiedNotification, setShowCopiedNotification] = useState(false)

    const firstRender = useRef(true)
    const history = useHistory()

    const [fields, setFields] = useState([
        {
            venue: '',
            capacity: '',
            date: '',
            time: '',
            hall: '',
            price: '',
            city: ''
        },
    ])

    const [lineups, setLineups] = useState([
        {
            artist_name: '',
            artist_type: ''
        },
        {
            artist_name: '',
            artist_type: ''
        }
    ])

    const [setlists, setSetlists] = useState([
        {
            artist_name: '',
            track: ''
        },
        {
            artist_name: '',
            track: ''
        }
    ])

    const requiredFields = {
        name: true,
        venue: true,
        capacity: true,
        date: true,
        hall: true,
        city: true,
        lineups: {
            artist_name: false,
            artist_type: false
        },
        setlists: {
            artist_name: false,
            track: false
        }
    }

    const addField = () => {
        setFields([
            ...fields,
            {
                venue: '',
                capacity: '',
                date: '',
                time: '',
                hall: '',
                price: '',
                city: '',
            }
        ])
    }

    const addSetlist = () => {
        setSetlists([
            ...setlists,
            {
                artist_name: '',
                track: ''
            }])
    }

    const addLineup = () => {
        setLineups([
            ...lineups,
            {
                artist_name: '',
                artist_type: ''
            }
        ])
    }

    const removeField = index => {
        setFields(fields.filter((item, i) => index !== i))
    }

    const removeLineup = index => {
        setLineups(lineups.filter((item, i) => index !== i))
    }

    const removeSetlist = index => {
        setSetlists(setlists.filter((item, i) => index !== i))
    }

    useEffect(() => {
        if (formStatus.error && formStatus.message) {
            setError(formStatus.message)
        }
    }, [formStatus])

    useEffect(() => {
        let max_accuracy = (fields.length * 7) + (lineups.length * 2) + (setlists.length * 2) + 1
        let accuracy = 0

        if (simName.length > 0 && !!category) {
            accuracy++
        }


        [fields, lineups, setlists].forEach(formSection => {
            formSection.forEach(field => {
                Object.values(field).forEach(item => {
                    if (item.length > 0) {
                        accuracy++
                    }
                })
            })
        })

        setAccuracy(`${((accuracy / max_accuracy) * 100).toFixed(0)}`)
    }, [fields, simName, lineups, setlists, category])

    const updateInput = (e, idx) => {
        const { name, value } = e.target

        if (name === 'name') {
            setSimName(value)
        } else if (name === 'description') {
            setDescription(value)
        } else {
            const currentFields = [...fields]
            currentFields[idx][name] = value
            setFields(currentFields)
        }
    }

    const updateDate = (date, idx) => {
        const formattedDate = date ? moment(date).format('DD.MM.YYYY') : ''
        const formattedTime = date ? moment(date).format('HH:mm') : ''
        const formattedTimezone = date ? Intl.DateTimeFormat().resolvedOptions().timeZone : ''

        const updatedFields = [...fields]
        updatedFields[idx].time = formattedTime
        updatedFields[idx].date = formattedDate

        setFields(updatedFields)
        setTz(formattedTimezone)
        setStartDate(date)
    }

    const submitForm = (e) => {
        e.preventDefault()
        setLoading(true)
        setError('')

        const fieldsWithCategory = fields.map(eachField => {
            return {
                category,
                ...eachField
            }
        })

        const { error, errMsg, resp: formDataArray } = formatData(simName, description, fieldsWithCategory, lineups, setlists, tz, t)
        if (error) {
            setError(errMsg)
            setLoading(false)
            return
        }

        const sendFormAsync = async () => {
            for (let i = 0; i < formDataArray.length; i++) {
                try {
                    await sendSimulationForm(updateFormStatus, formDataArray[i])
                } catch (error) {
                    setError(error.message)
                }
            }
        }
        sendFormAsync()
        setLoading(false)
    }

    useEffect(() => {
        // timeout used to allow user time to see that submission is successful 
        let timeoutId
        if (formStatus.success) {
            addNotification({ message: t('Toasts.Simulation.eventAdded.message'), description: t('Toasts.Simulation.eventAdded.description') })
            timeoutId = setTimeout(() => {
                history.push('/')
            }, 2000)
        }
        return () => clearTimeout(timeoutId)
    }, [formStatus.success, addNotification, history, t])

    useEffect(() => {
        // Determine if fields were changed in any way while skipping the page's first render.
        if (firstRender.current) {
            firstRender.current = false
            return
        }
    }, [fields, category, simName, setlists, lineups])

    const closeModalAndShowNotification = () => {
        setCopyModalOpen()
        setShowCopiedNotification(true)

        setTimeout(() => setShowCopiedNotification(false), 3000)
    }

    const handleSetlistUpdate = (newSetlists) => {
        setSetlists(newSetlists)
    }
    const handleLineupsUpdate = (newLineups) => {
        setLineups(newLineups)
    }

    return (
        <form className="simulation-form my-4" onSubmit={e => e.preventDefault()} noValidate>
            <>
                <SessionTimer classes='mx-lg-0' />
                <SimulationFormHeader accuracy={accuracy} />
                <div className="simulation-form__section simulation-form__key-facts">
                    <Card className="card-default mb-0">
                        <div className="card-header">
                            <h2 className="card-title-default">{t('Simulation.nameInputTitle')}</h2>
                        </div>
                        <Card.Body>
                            <div className="simulation-form_header-wrap">
                                <div className="requiredInputBox simulation-form_sim-name-wrap">
                                    <Input
                                        placeholder={t('Simulation.form.eventName')}
                                        separatePlaceholder={true}
                                        value={simName}
                                        name="name"
                                        onChange={e => updateInput(e)}
                                        required
                                    />
                                </div>
                                <div className="requiredInputBox simulation-form_sim-category-wrap">
                                    <DropDown
                                        onChange={setCategory}
                                        value={category}
                                        options={eventCategories}
                                        placeholder={t('Simulation.form.category')}
                                    />
                                </div>
                            </div>
                            <Textarea
                                classNames="mb-0 mt-3 mt-md-0"
                                placeholder={t('Simulation.form.description')}
                                separatePlaceholder
                                value={description ?? ''}
                                name="description"
                                onChange={e => updateInput(e)}
                                maxLength={2500}
                                showMax
                                rows={5}
                            />
                        </Card.Body>
                    </Card>
                </div>

                {fields.map((f, idx) => {
                    return (
                        <KeyFactsForm
                            key={`f-${idx}`}
                            startDate={startDate}
                            updateDate={updateDate}
                            requiredFields={requiredFields}
                            fields={f}
                            updateInput={updateInput}
                            index={idx}
                            removeField={removeField}
                        />
                    )
                })
                }
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        className="lineup__add"
                        onClick={addField}
                        aria-label={t('Simulation.form.addDatesAndVenues')}
                    >
                        <span>+</span>
                        {t('Simulation.form.addDatesAndVenues')}
                    </button>
                </div>

                <div className="simulation-form__section simulation-form__programme">
                    <Card className="card-default mb-4">
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '1em', flexWrap: 'wrap' }}>
                            <h2 className="card-title-default">{t('Simulation.artistFormTitle')}</h2>

                            <div className='display-flex'>
                                <SecondaryButton
                                    classNames="simulation-btn"
                                    text={t('EventEditor.copyFromEvent')}
                                    onClick={() => setCopyModalOpen(true)}
                                    showIcon={false}
                                    isBackArrow={false}
                                    color={'navy'}
                                    size={'small'}
                                />
                                <div>
                                    <InfoBox content={'Tips.EventEdit.copySetlistLineupToolTip'} />
                                </div>
                            </div>
                        </div>
                        <Card.Body className='d-flex justify-content-center flex-column gap-5'>
                            <div className=" d-flex lineup__header">
                                <h5 className="lineup__title">{t('Simulation.artists')}</h5>
                            </div>
                            {lineups.map((lineup, index) => (
                                <Artist
                                    key={index}
                                    removeArtist={removeLineup}
                                    setFields={setLineups}
                                    artistIndex={index}
                                    requiredFields={requiredFields}
                                    lineup={lineup}
                                    lineups={lineups}
                                />
                            ))}

                            <button
                                type="button"
                                className="lineup__add"
                                onClick={addLineup}
                                aria-label={t('Simulation.form.addArtist')}
                            >
                                <span>+</span>
                                {t('Simulation.form.addArtist')}
                            </button>

                        </Card.Body>
                        <Card.Body className='d-flex justify-content-center flex-column gap-5'>
                            <div className=" d-flex lineup__header">
                                <h5 className="lineup__title">{t('Simulation.programme')}</h5>
                            </div>
                            {setlists.map((setlist, index) => (
                                <SetList
                                    key={index}
                                    removeSetlist={removeSetlist}
                                    setFields={setSetlists}
                                    setlists={setlists}
                                    setlist={setlist}
                                    setlistIndex={index}
                                    requiredFields={requiredFields}
                                />
                            ))}

                            <button
                                type="button"
                                className="lineup__add"
                                onClick={addSetlist}
                                aria-label={t('Simulation.form.addWork')}
                            >
                                <span>+</span>
                                {t('Simulation.form.addWork')}
                            </button>

                        </Card.Body>
                    </Card>
                </div>
                <div className="row mb-md-5 mb-lg-0">
                    <div className="col-12 mb-md-5">
                        <div className='d-flex flex-row justify-content-end mb-md-4'>
                            <div>
                                {loading ?
                                    <LoadingSpinner title={t('common.sending')} portalBackground={true} />
                                    :
                                    <PrimaryButton
                                        type='button'
                                        onClick={submitForm}
                                        text={t('Simulation.form.btn')}
                                        disabled={loading || formStatus.success}
                                        classNames="simulation-btn"
                                    />
                                }
                            </div>
                        </div>
                        {error && <ErrorMessage danger>{t(error)}</ErrorMessage>}
                    </div>
                </div>
            </>
            {!!copyModalOpen && (
                <PortalModal
                    isOpen={copyModalOpen}
                    closeCallback={() => setCopyModalOpen(false)}
                    onClickOutside={() => setCopyModalOpen(false)}
                >
                    <CopyPasteContent
                        currentSetlists={setlists.filter(x => x && (!!x.artist_name || !!x.track))}
                        currentLineups={lineups.filter(x => x && (!!x.artist_name || !!x.artist_type))}
                        setSetlists={handleSetlistUpdate}
                        setLineups={handleLineupsUpdate}
                        eventTitle={simName}
                        closeModalAndShowNotification={closeModalAndShowNotification}
                        closeModal={setCopyModalOpen}
                    />
                </PortalModal>
            )}
            {showCopiedNotification &&
                <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, width: '100%', textAlign: 'center' }}>
                    <h1 className="main-title hide-mobile ml-4">
                        {t('common.copied')}
                    </h1>
                </div>
            }
        </form>
    )
}

SimulationForm.propTypes = {
}

export default SimulationForm
